/**
 * FloorTransitionScene - Scene shown between floors
 * Displays rewards, floor info, and allows player to proceed
 */

import Phaser from 'phaser';
import { GameStateManager } from '../game/GameStateManager';
import { SCENES, GAME_CONFIG, COLORS, Z_DEPTHS } from '../constants/GameConstants';

export class FloorTransitionScene extends Phaser.Scene {
  private gameStateManager!: GameStateManager;

  constructor() {
    super({ key: SCENES.FLOOR_TRANSITION });
  }

  init(): void {
    this.gameStateManager = GameStateManager.getInstance();
  }

  create(): void {
    const { WIDTH, HEIGHT } = GAME_CONFIG;
    const centerX = WIDTH / 2;
    const centerY = HEIGHT / 2;

    // Background
    this.add.rectangle(0, 0, WIDTH, HEIGHT, COLORS.BACKGROUND).setOrigin(0);

    // Get current floor info
    const currentFloor = this.gameStateManager.getCurrentFloor();
    const floorData = this.gameStateManager.getCurrentFloorData();
    const isComplete = this.gameStateManager.isRunComplete();

    if (isComplete) {
      // Run completed!
      this.showVictoryScreen(centerX, centerY);
      return;
    }

    // Title
    const title = this.add.text(centerX, 100, `Floor ${currentFloor}`, {
      fontSize: '48px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    title.setDepth(Z_DEPTHS.UI);

    // Floor type indicator
    const typeText = this.getFloorTypeText(floorData.type);
    const typeColor = this.getFloorTypeColor(floorData.type);
    const subtitle = this.add.text(centerX, 160, typeText, {
      fontSize: '24px',
      color: typeColor,
    });
    subtitle.setOrigin(0.5);
    subtitle.setDepth(Z_DEPTHS.UI);

    // Stats display
    const statsY = 380;
    const gameState = this.gameStateManager.getGameState();
    const statsText = [
      `Floor: ${currentFloor - 1}/${gameState.currentRun?.totalFloors || 12}`,
      `Gold: ${gameState.gold}`,
      `Relics: ${gameState.relics.length}`,
    ].join('  |  ');

    const stats = this.add.text(centerX, statsY, statsText, {
      fontSize: '20px',
      color: '#cccccc',
    });
    stats.setOrigin(0.5);
    stats.setDepth(Z_DEPTHS.UI);

    // Next floor preview
    const previewY = 480;
    const previewTitle = this.add.text(centerX, previewY, 'Next Floor:', {
      fontSize: '28px',
      color: '#ffffff',
    });
    previewTitle.setOrigin(0.5);
    previewTitle.setDepth(Z_DEPTHS.UI);

    const nextFloorType = this.getFloorTypeText(floorData.type);
    const preview = this.add.text(centerX, previewY + 40, nextFloorType, {
      fontSize: '20px',
      color: this.getFloorTypeColor(floorData.type),
    });
    preview.setOrigin(0.5);
    preview.setDepth(Z_DEPTHS.UI);

    const difficulty = this.add.text(
      centerX,
      previewY + 70,
      `Difficulty: ${floorData.difficulty.toUpperCase()}`,
      {
        fontSize: '18px',
        color: this.getDifficultyColor(floorData.difficulty),
      }
    );
    difficulty.setOrigin(0.5);
    difficulty.setDepth(Z_DEPTHS.UI);

    // Continue button
    this.createContinueButton(centerX, HEIGHT - 150);

    // Save game
    this.gameStateManager.saveGame();
  }

  private createContinueButton(x: number, y: number): void {
    const button = this.add.rectangle(x, y, 300, 70, COLORS.BUTTON);
    button.setStrokeStyle(2, COLORS.BUTTON_HOVER);
    button.setInteractive({ useHandCursor: true });
    button.setDepth(Z_DEPTHS.UI);

    const buttonText = this.add.text(x, y, 'CONTINUE', {
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5);
    buttonText.setDepth(Z_DEPTHS.UI + 1);

    // Hover effects
    button.on('pointerover', () => {
      button.setFillStyle(COLORS.BUTTON_HOVER);
      buttonText.setScale(1.1);
    });

    button.on('pointerout', () => {
      button.setFillStyle(COLORS.BUTTON);
      buttonText.setScale(1);
    });

    // Click handler
    button.on('pointerdown', () => {
      this.proceedToNextFloor();
    });
  }

  private proceedToNextFloor(): void {
    // Advance to next floor
    this.gameStateManager.advanceFloor();

    const floorData = this.gameStateManager.getCurrentFloorData();

    // Start appropriate scene based on encounter type
    switch (floorData.encounter) {
      case 'battle':
        this.scene.start(SCENES.BATTLE, { floorData });
        break;
      case 'shop':
        this.scene.start(SCENES.SHOP);
        break;
      case 'rest':
        this.scene.start(SCENES.HEAL);
        break;
      case 'treasure':
        this.scene.start(SCENES.TREASURE);
        break;
      default:
        this.scene.start(SCENES.BATTLE, { floorData });
        break;
    }
  }

  private showVictoryScreen(centerX: number, centerY: number): void {
    const title = this.add.text(centerX, centerY - 100, 'Victory!', {
      fontSize: '64px',
      color: '#ffd700',
      fontStyle: 'bold',
    });
    title.setOrigin(0.5);
    title.setDepth(Z_DEPTHS.UI);

    const subtitle = this.add.text(centerX, centerY, 'You have conquered all floors!', {
      fontSize: '28px',
      color: '#ffffff',
    });
    subtitle.setOrigin(0.5);
    subtitle.setDepth(Z_DEPTHS.UI);

    const gameState = this.gameStateManager.getGameState();
    const statsText = `Floors Cleared: ${gameState.currentRun?.floorsCleared || 0}\nGold Earned: ${gameState.gold}`;
    const stats = this.add.text(centerX, centerY + 80, statsText, {
      fontSize: '20px',
      color: '#cccccc',
      align: 'center',
    });
    stats.setOrigin(0.5);
    stats.setDepth(Z_DEPTHS.UI);

    // Return to menu button
    const button = this.add.rectangle(centerX, centerY + 200, 300, 70, COLORS.BUTTON);
    button.setStrokeStyle(2, COLORS.BUTTON_HOVER);
    button.setInteractive({ useHandCursor: true });
    button.setDepth(Z_DEPTHS.UI);

    const buttonText = this.add.text(centerX, centerY + 200, 'MAIN MENU', {
      fontSize: '32px',
      color: '#ffffff',
      fontStyle: 'bold',
    });
    buttonText.setOrigin(0.5);
    buttonText.setDepth(Z_DEPTHS.UI + 1);

    button.on('pointerover', () => {
      button.setFillStyle(COLORS.BUTTON_HOVER);
    });

    button.on('pointerout', () => {
      button.setFillStyle(COLORS.BUTTON);
    });

    button.on('pointerdown', () => {
      this.gameStateManager.endRun(true);
      this.scene.start(SCENES.MENU);
    });
  }

  private getFloorTypeText(type: string): string {
    switch (type) {
      case 'combat':
        return 'Combat Encounter';
      case 'elite':
        return 'Elite Battle';
      case 'boss':
        return 'BOSS FIGHT';
      case 'event':
        return 'Special Event';
      default:
        return 'Unknown';
    }
  }

  private getFloorTypeColor(type: string): string {
    switch (type) {
      case 'combat':
        return '#ffffff';
      case 'elite':
        return '#ff8800';
      case 'boss':
        return '#ff0000';
      case 'event':
        return '#00ff00';
      default:
        return '#ffffff';
    }
  }

  private getDifficultyColor(difficulty: string): string {
    switch (difficulty) {
      case 'easy':
        return '#00ff00';
      case 'medium':
        return '#ffff00';
      case 'hard':
        return '#ff8800';
      case 'expert':
        return '#ff0000';
      default:
        return '#ffffff';
    }
  }
}
